import React, { useState, useEffect } from "react";
import { useAppSelector } from "../store";
import { selectors } from "../store/selectors";
import * as derivedSelectors from "../store/selectors/derivedSelectors";
import {
  calculateShipStats,
  calculatePowerPlantStats,
  calculateWeaponStats,
  calculateStateroomStats,
  calculateCrewRequirements,
  calculateTotalCost,
  calculateTotalWeight,
  calculatePowerRequired,
  calculateHullCost,
  calculateHullPoints,
  calculateArmorCost,
  calculateArmorTons,
  calculateBridgeSize,
  calculateSensorsTons,
} from "../utils/calculations";

import { CalculationResult as CalculationResultType } from "../types/calculations";
import { formatCurrency } from "../utils/formatting";
import { TonnageDebugger } from "./TonnageDebugger";
import { calculateSensorCost } from "../constants/sensors";
import { PowerDebugger } from "./PowerDebugger";
import { CostDebugger } from "./CostDebugger";

interface DebugCalculationResult {
  name: string;
  input: Record<string, unknown>;
  output: CalculationResultType<unknown>;
  executionTime: number;
  error?: string;
}

const DebugPage: React.FC = () => {
  const [calculations, setCalculations] = useState<DebugCalculationResult[]>(
    [],
  );
  const [isVisible, setIsVisible] = useState(true); // Always visible when accessed via route
  const [activeTab, setActiveTab] = useState("overview");

  // Get all the state data
  const shipBuilderState = useAppSelector(
    selectors.shipBuilder.selectShipBuilder,
  );
  const optionalSystems = useAppSelector((state) => state.optionalSystems);
  const shipStats = useAppSelector(selectors.shipBuilder.selectShipStats);
  
  // Get all Redux slices
  const fullState = useAppSelector((state) => state);

  // Access derived selectors individually - using more specific selectors to avoid warnings
  const maintenanceCost = useAppSelector(
    derivedSelectors.selectMaintenanceCost,
  );
  const totalPowerAvailable = useAppSelector(
    derivedSelectors.selectTotalPowerAvailable,
  );
  const powerEfficiency = useAppSelector(
    derivedSelectors.selectPowerEfficiency,
  );
  const totalCrewCost = useAppSelector(derivedSelectors.selectTotalCrewCost);
  const totalMonthlyCosts = useAppSelector(
    derivedSelectors.selectTotalMonthlyCosts,
  );
  const cargoEfficiency = useAppSelector(
    derivedSelectors.selectCargoEfficiency,
  );
  const jumpEfficiency = useAppSelector(derivedSelectors.selectJumpEfficiency);
  const totalOperatingCosts = useAppSelector(
    derivedSelectors.selectTotalOperatingCosts,
  );
  const totalAnnualCosts = useAppSelector(
    derivedSelectors.selectTotalAnnualCosts,
  );

  // Run all calculations and measure performance
  useEffect(() => {
    if (!isVisible) return;

    const runCalculations = () => {
      const results: DebugCalculationResult[] = [];

      // 1. Main Ship Stats Calculation
      const startTime = performance.now();
      const shipStatsResult = calculateShipStats(shipBuilderState, optionalSystems);
      const endTime = performance.now();

      results.push({
        name: "Ship Stats Calculation",
        input: {
          shipSize: shipBuilderState.shipSize,
          hull: shipBuilderState.hull,
          drives: shipBuilderState.drives,
          powerPlant: shipBuilderState.powerPlant,
          weapons: shipBuilderState.weapons,
          staterooms: shipBuilderState.staterooms,
          crew: shipBuilderState.crew,
          techLevel: shipBuilderState.techLevel,
        },
        output: shipStatsResult,
        executionTime: endTime - startTime,
        error: shipStatsResult.success
          ? undefined
          : shipStatsResult.error?.message,
      });

      // 2. Power Plant Calculations
      if (shipBuilderState.powerPlant) {
        const powerPlantStart = performance.now();
        const powerPlantResult = calculatePowerPlantStats(
          shipBuilderState.powerPlant,
          shipBuilderState.shipSize || 0,
        );
        const powerPlantEnd = performance.now();

        results.push({
          name: "Power Plant Stats",
          input: {
            powerPlant: shipBuilderState.powerPlant,
            shipSize: shipBuilderState.shipSize,
          },
          output: powerPlantResult,
          executionTime: powerPlantEnd - powerPlantStart,
          error: powerPlantResult.success
            ? undefined
            : powerPlantResult.error?.message,
        });
      }

      // 3. Weapon Calculations
      if (
        shipBuilderState.weaponSelections &&
        shipBuilderState.weaponSelections.length > 0
      ) {
        const weaponStart = performance.now();
        const weaponResult = calculateWeaponStats(
          shipBuilderState.weaponSelections,
          {},
        );
        const weaponEnd = performance.now();

        results.push({
          name: "Weapon Stats",
          input: {
            weapons: shipBuilderState.weaponSelections,
            weaponCounts: {},
          },
          output: weaponResult,
          executionTime: weaponEnd - weaponStart,
          error: weaponResult.success ? undefined : weaponResult.error?.message,
        });
      }

      // 4. Stateroom Calculations
      if (shipBuilderState.stateroomConfig) {
        const stateroomStart = performance.now();
        const stateroomResult = calculateStateroomStats(
          shipBuilderState.stateroomConfig,
        );
        const stateroomEnd = performance.now();

        results.push({
          name: "Stateroom Stats",
          input: {
            stateroomConfig: shipBuilderState.stateroomConfig,
          },
          output: stateroomResult,
          executionTime: stateroomEnd - stateroomStart,
          error: stateroomResult.success
            ? undefined
            : stateroomResult.error?.message,
        });
      }

      // 5. Crew Requirements
      const crewStart = performance.now();
      const crewResult = calculateCrewRequirements(shipBuilderState);
      const crewEnd = performance.now();

      results.push({
        name: "Crew Requirements",
        input: {
          shipBuilderState,
        },
        output: crewResult,
        executionTime: crewEnd - crewStart,
        error: crewResult.success ? undefined : crewResult.error?.message,
      });

      // 6. Total Cost Calculation
      const costStart = performance.now();
      const costResult = calculateTotalCost(shipBuilderState, {});
      const costEnd = performance.now();

      results.push({
        name: "Total Cost",
        input: {
          shipBuilderState,
        },
        output: costResult,
        executionTime: costEnd - costStart,
        error: costResult.success ? undefined : costResult.error?.message,
      });

      // 7. Total Weight Calculation
      const weightStart = performance.now();
      const weightResult = calculateTotalWeight(shipBuilderState, {});
      const weightEnd = performance.now();

      results.push({
        name: "Total Weight",
        input: {
          shipBuilderState,
        },
        output: weightResult,
        executionTime: weightEnd - weightStart,
        error: weightResult.success ? undefined : weightResult.error?.message,
      });

      // 8. Power Required Calculation
      const powerStart = performance.now();
      const powerResult = calculatePowerRequired(shipBuilderState, {});
      const powerEnd = performance.now();

      results.push({
        name: "Power Required",
        input: {
          shipBuilderState,
          weaponCounts: {},
        },
        output: powerResult,
        executionTime: powerEnd - powerStart,
        error: powerResult.success ? undefined : powerResult.error?.message,
      });

      // 9. Bridge Size Calculation
      if (shipBuilderState.shipSize) {
        const bridgeStart = performance.now();
        const bridgeSize = calculateBridgeSize(shipBuilderState.shipSize);
        const bridgeEnd = performance.now();

        results.push({
          name: "Bridge Size",
          input: {
            shipSize: shipBuilderState.shipSize,
          },
          output: {
            success: true,
            data: bridgeSize,
          },
          executionTime: bridgeEnd - bridgeStart,
        });
      }

      // 10. Sensor Calculations
      if (shipBuilderState.sensors) {
        const sensorsStart = performance.now();
        const sensorCost = calculateSensorCost(shipBuilderState.sensors.type as any, shipBuilderState.shipSize || 100);
        const sensorTons = calculateSensorsTons(shipBuilderState.sensors.type);
        const sensorsEnd = performance.now();

        results.push({
          name: "Sensor Stats",
          input: {
            sensors: shipBuilderState.sensors,
          },
          output: {
            success: true,
            data: {
              cost: sensorCost,
              tons: sensorTons,
            },
          },
          executionTime: sensorsEnd - sensorsStart,
        });
      }

      // 11. Hull Cost and Points
      if (shipBuilderState.hull && shipBuilderState.hull.configuration) {
        const hullStart = performance.now();
        const hullCost = calculateHullCost(
          shipBuilderState.hull.size,
          shipBuilderState.hull
            .configuration as import("../types/hull").HullConfiguration,
        );
        const hullPoints = calculateHullPoints(
          shipBuilderState.hull.size,
          shipBuilderState.hull
            .configuration as import("../types/hull").HullConfiguration,
        );
        const hullEnd = performance.now();

        results.push({
          name: "Hull Stats",
          input: {
            hull: shipBuilderState.hull,
          },
          output: {
            success: true,
            data: {
              cost: hullCost,
              hullPoints: hullPoints,
            },
          },
          executionTime: hullEnd - hullStart,
        });
      }

      // 12. Armor Cost and Tons
      if (shipBuilderState.armor) {
        const armorStart = performance.now();
        const armorCost = calculateArmorCost(
          shipBuilderState.armor.type as import("../types/armor").ArmorType,
          shipBuilderState.shipSize || 0,
        );
        const armorTons = calculateArmorTons(
          shipBuilderState.armor.type as import("../types/armor").ArmorType,
          shipBuilderState.shipSize || 0,
        );
        const armorEnd = performance.now();

        results.push({
          name: "Armor Stats",
          input: {
            armor: shipBuilderState.armor,
            shipSize: shipBuilderState.shipSize,
          },
          output: {
            success: true,
            data: {
              cost: armorCost,
              tons: armorTons,
            },
          },
          executionTime: armorEnd - armorStart,
        });
      }

      setCalculations(results);
    };

    runCalculations();
  }, [isVisible, shipBuilderState, optionalSystems]);

  const toggleVisibility = () => {
    setIsVisible(!isVisible);
  };

  const renderValue = (value: unknown): JSX.Element => {
    if (value === null || value === undefined) {
      return <span className="text-gray-500">N/A</span>;
    }
    if (typeof value === "object") {
      return (
        <pre className="text-xs bg-gray-800 p-2 rounded-md overflow-x-auto">
          {JSON.stringify(value, null, 2)}
        </pre>
      );
    }
    return <span className="text-white">{String(value)}</span>;
  };

  return (
    <div>
      {isVisible && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-80 flex items-center justify-center z-40">
          <div className="bg-gray-800 text-white w-11/12 h-5/6 max-w-6xl rounded-lg shadow-2xl flex flex-col">
            <div className="flex justify-between items-center p-4 border-b border-gray-700">
              <h2 className="text-2xl font-bold">Debug Panel</h2>
              <button
                onClick={toggleVisibility}
                className="text-gray-400 hover:text-white"
              >
                &times;
              </button>
            </div>
            <div className="flex-grow flex overflow-hidden">
              <div className="w-1/4 p-4 border-r border-gray-700 overflow-y-auto">
                <h3 className="text-lg font-semibold mb-4">Navigation</h3>
                <ul>
                  <li
                    className={`p-2 cursor-pointer rounded ${
                      activeTab === "overview" ? "bg-blue-600" : ""
                    }`}
                    onClick={() => setActiveTab("overview")}
                  >
                    Overview
                  </li>
                  <li
                    className={`p-2 cursor-pointer rounded ${
                      activeTab === "state" ? "bg-blue-600" : ""
                    }`}
                    onClick={() => setActiveTab("state")}
                  >
                    Full State
                  </li>
                  <li
                    className={`p-2 cursor-pointer rounded ${
                      activeTab === "derived" ? "bg-blue-600" : ""
                    }`}
                    onClick={() => setActiveTab("derived")}
                  >
                    Derived Selectors
                  </li>
                  <li
                    className={`p-2 cursor-pointer rounded ${
                      activeTab === "calculations" ? "bg-blue-600" : ""
                    }`}
                    onClick={() => setActiveTab("calculations")}
                  >
                    Calculations
                  </li>
                  <li
                    className={`p-2 cursor-pointer rounded ${
                      activeTab === "tonnage" ? "bg-blue-600" : ""
                    }`}
                    onClick={() => setActiveTab("tonnage")}
                  >
                    Tonnage Debug
                  </li>
                  <li
                    className={`p-2 cursor-pointer rounded ${
                      activeTab === "power" ? "bg-blue-600" : ""
                    }`}
                    onClick={() => setActiveTab("power")}
                  >
                    Power Debug
                  </li>
                  <li
                    className={`p-2 cursor-pointer rounded ${
                      activeTab === "cost" ? "bg-blue-600" : ""
                    }`}
                    onClick={() => setActiveTab("cost")}
                  >
                    Cost Debug
                  </li>
                </ul>
              </div>
              <div className="w-3/4 p-4 overflow-y-auto">
                {activeTab === "overview" && (
                  <div>
                    <h3 className="text-xl font-bold mb-4">Overview</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-gray-700 p-3 rounded">
                        <div className="font-semibold">Total Cost:</div>
                        <div>
                          {renderValue(formatCurrency(shipStats.totalCost))}
                        </div>
                      </div>
                      <div className="bg-gray-700 p-3 rounded">
                        <div className="font-semibold">Total Weight:</div>
                        <div>{renderValue(shipStats.totalWeight)} tons</div>
                      </div>
                      <div className="bg-gray-700 p-3 rounded">
                        <div className="font-semibold">Power Required:</div>
                        <div>{renderValue(shipStats.powerRequired)} Power</div>
                      </div>
                      <div className="bg-gray-700 p-3 rounded">
                        <div className="font-semibold">Power Available:</div>
                        <div>{renderValue(totalPowerAvailable)} Power</div>
                      </div>
                      <div className="bg-gray-700 p-3 rounded">
                        <div className="font-semibold">Cargo Space:</div>
                        <div>{renderValue(shipStats.cargoSpace)} tons</div>
                      </div>
                    </div>
                  </div>
                )}
                {activeTab === "state" && (
                  <div>
                    <h3 className="text-xl font-bold mb-4">Redux State by Slice</h3>
                    <div className="space-y-6">
                      {/* Ship Builder Slice */}
                      <div className="bg-gray-700 p-4 rounded-lg">
                        <h4 className="text-lg font-semibold mb-3 text-blue-300">Ship Builder Slice</h4>
                        <div className="max-h-96 overflow-y-auto">
                          {renderValue(fullState.shipBuilder)}
                        </div>
                      </div>
                      
                      {/* Optional Systems Slice */}
                      <div className="bg-gray-700 p-4 rounded-lg">
                        <h4 className="text-lg font-semibold mb-3 text-green-300">Optional Systems Slice</h4>
                        <div className="max-h-96 overflow-y-auto">
                          {renderValue(fullState.optionalSystems)}
                        </div>
                      </div>
                      
                      {/* UI Slice */}
                      <div className="bg-gray-700 p-4 rounded-lg">
                        <h4 className="text-lg font-semibold mb-3 text-yellow-300">UI Slice</h4>
                        <div className="max-h-96 overflow-y-auto">
                          {renderValue(fullState.ui)}
                        </div>
                      </div>
                      
                      {/* Breakaway Hull Slice */}
                      <div className="bg-gray-700 p-4 rounded-lg">
                        <h4 className="text-lg font-semibold mb-3 text-purple-300">Breakaway Hull Slice</h4>
                        <div className="max-h-96 overflow-y-auto">
                          {renderValue(fullState.breakawayHull)}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {activeTab === "derived" && (
                  <div>
                    <h3 className="text-xl font-bold mb-4">
                      Derived Selectors
                    </h3>
                    <div className="space-y-2">
                      <div>
                        Maintenance Cost: {renderValue(maintenanceCost)}
                      </div>
                      <div>
                        Total Power Available:{" "}
                        {renderValue(totalPowerAvailable)}
                      </div>
                      <div>
                        Power Efficiency: {renderValue(powerEfficiency)}
                      </div>
                      <div>Total Crew Cost: {renderValue(totalCrewCost)}</div>
                      <div>
                        Total Monthly Costs: {renderValue(totalMonthlyCosts)}
                      </div>
                      <div>
                        Cargo Efficiency: {renderValue(cargoEfficiency)}
                      </div>
                      <div>Jump Efficiency: {renderValue(jumpEfficiency)}</div>
                      <div>
                        Total Operating Costs:{" "}
                        {renderValue(totalOperatingCosts)}
                      </div>
                      <div>
                        Total Annual Costs: {renderValue(totalAnnualCosts)}
                      </div>
                    </div>
                  </div>
                )}
                {activeTab === "calculations" && (
                  <div>
                    <h3 className="text-xl font-bold mb-4">
                      Calculation Performance
                    </h3>
                    <div className="space-y-4">
                      {calculations.map((calc, index) => (
                        <div key={index} className="bg-gray-700 p-4 rounded-lg">
                          <div className="flex justify-between items-center">
                            <div className="font-bold text-lg">{calc.name}</div>
                            <div
                              className={`text-sm font-semibold px-2 py-1 rounded-full ${
                                calc.executionTime < 5
                                  ? "bg-green-600"
                                  : "bg-yellow-600"
                              }`}
                            >
                              {calc.executionTime.toFixed(2)} ms
                            </div>
                          </div>
                          {calc.error && (
                            <div className="text-red-400 mt-2">
                              Error: {calc.error}
                            </div>
                          )}
                          <div className="mt-2">
                            <h4 className="font-semibold">Input:</h4>
                            {renderValue(calc.input)}
                          </div>
                          <div className="mt-2">
                            <h4 className="font-semibold">Output:</h4>
                            {renderValue(calc.output)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {activeTab === "tonnage" && (
                  <div>
                    <h3 className="text-xl font-bold mb-4">Tonnage Breakdown</h3>
                    <TonnageDebugger />
                  </div>
                )}
                {activeTab === "power" && (
                  <div>
                    <h3 className="text-xl font-bold mb-4">Power Generation & Consumption</h3>
                    <PowerDebugger />
                  </div>
                )}
                {activeTab === "cost" && (
                  <div>
                    <h3 className="text-xl font-bold mb-4">Ship Cost Breakdown</h3>
                    <CostDebugger />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DebugPage;
